import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu, Loader2,
  LayoutDashboard, BarChart3, Workflow, Activity, Store,
  Play, Pause, Square, RotateCcw, ChevronRight, Clock,
  CheckCircle2, AlertTriangle, XCircle, Eye, ArrowLeft
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgentMonitor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentMetrics, setAgentMetrics] = useState(null);
  const [agentRuns, setAgentRuns] = useState([]);
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { fetchAgents(); }, []);

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API}/agents`);
      setAgents(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openAgent = useCallback(async (agent) => {
    setSelectedAgent(agent);
    setSelectedTrace(null);
    setDetailLoading(true);
    try {
      const [metricsRes, runsRes] = await Promise.all([
        axios.get(`${API}/agents/${agent.id}/metrics`),
        axios.get(`${API}/agents/${agent.id}/runs`),
      ]);
      setAgentMetrics(metricsRes.data);
      setAgentRuns(runsRes.data);
    } catch (err) { console.error(err); }
    finally { setDetailLoading(false); }
  }, []);

  const openTrace = async (agentId, runId) => {
    try {
      const res = await axios.get(`${API}/agents/${agentId}/runs/${runId}/trace`);
      setSelectedTrace(res.data);
    } catch (err) { toast.error('Failed to load trace'); }
  };

  const handleAction = async (agentId, action) => {
    try {
      await axios.post(`${API}/agents/${agentId}/${action}`);
      toast.success(`Agent ${action}d`);
      fetchAgents();
      if (selectedAgent?.id === agentId) {
        const res = await axios.get(`${API}/agents/${agentId}`);
        setSelectedAgent({ ...selectedAgent, ...res.data });
        openAgent({ ...selectedAgent, ...res.data });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: BarChart3, label: 'Demand Intel', path: '/demand' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: Activity, label: 'Agent Monitor', path: '/agents' },
  ];

  const getStatusBadge = (status, health) => {
    if (health?.status === 'needs_attention') return { cls: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'NEEDS ATTENTION' };
    if (health?.status === 'warning') return { cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'WARNING' };
    if (status === 'running') return { cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', label: 'LIVE' };
    if (status === 'paused') return { cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'PAUSED' };
    return { cls: 'bg-white/10 text-muted-foreground border-white/10', label: 'STOPPED' };
  };

  const getRunStatus = (status) => {
    if (status === 'completed') return { icon: CheckCircle2, cls: 'text-emerald-400' };
    if (status === 'failed') return { icon: XCircle, cls: 'text-red-400' };
    return { icon: Clock, cls: 'text-amber-400' };
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <HSLogo size={32} />
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (<Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}><Icon className="w-5 h-5" />{item.label}</Link>);
            })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">{user?.name?.charAt(0) || 'U'}</div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-muted-foreground truncate">{user?.email}</p></div>
            </div>
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Sign Out</Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              {selectedAgent && <button onClick={() => { setSelectedAgent(null); setSelectedTrace(null); }} className="p-1.5 text-muted-foreground hover:text-white"><ArrowLeft className="w-5 h-5" /></button>}
              <h1 className="text-lg font-bold" data-testid="monitor-title">{selectedAgent ? selectedAgent.name : 'Agent Monitor'}</h1>
            </div>
            <span className="text-xs font-mono px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              {agents.filter(a => a.status === 'running').length}/{agents.length} LIVE
            </span>
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div> : !selectedAgent ? (
            /* AGENT TABLE */
            <div className="space-y-4" data-testid="agent-table">
              {agents.length === 0 ? (
                <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
                  <Activity className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <h3 className="text-lg font-bold mb-2">No agents deployed</h3>
                  <p className="text-muted-foreground text-sm mb-4">Deploy a workflow from the Workflow Builder to start monitoring.</p>
                  <Button onClick={() => navigate('/workflow')} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm">Build Workflow</Button>
                </div>
              ) : (
                <div className="bg-[#0a0a0a] border border-white/5 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/5">
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Agent</th>
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Status</th>
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Version</th>
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Last Run</th>
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Runs 24h</th>
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Usage 24h</th>
                          <th className="text-right p-4 text-xs text-muted-foreground uppercase font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {agents.map((agent) => {
                          const badge = getStatusBadge(agent.status, agent.health);
                          const usageStr = Object.entries(agent.usage_last_24h || {}).map(([k, v]) => `${k}:${v.units}`).join(', ') || '-';
                          return (
                            <tr key={agent.id} className="hover:bg-white/5 transition-colors cursor-pointer" onClick={() => openAgent(agent)} data-testid={`agent-row-${agent.id}`}>
                              <td className="p-4"><p className="font-medium text-sm">{agent.name}</p></td>
                              <td className="p-4"><span className={`text-[10px] font-mono px-1.5 py-0.5 border ${badge.cls}`}>{badge.label}</span></td>
                              <td className="p-4 text-sm text-muted-foreground font-mono">v{agent.blueprint_version}</td>
                              <td className="p-4 text-sm text-muted-foreground">{agent.last_run_at ? new Date(agent.last_run_at).toLocaleString() : '-'}</td>
                              <td className="p-4 font-mono text-sm">{agent.runs_last_24h}</td>
                              <td className="p-4 text-xs text-muted-foreground font-mono">{usageStr}</td>
                              <td className="p-4 text-right">
                                <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                                  {agent.status === 'running' && (
                                    <button onClick={() => handleAction(agent.id, 'pause')} className="p-1.5 text-muted-foreground hover:text-amber-400" title="Pause" data-testid={`pause-${agent.id}`}><Pause className="w-4 h-4" /></button>
                                  )}
                                  {agent.status === 'paused' && (
                                    <button onClick={() => handleAction(agent.id, 'resume')} className="p-1.5 text-muted-foreground hover:text-emerald-400" title="Resume" data-testid={`resume-${agent.id}`}><Play className="w-4 h-4" /></button>
                                  )}
                                  {agent.status !== 'running' && (
                                    <button onClick={() => handleAction(agent.id, 'restart')} className="p-1.5 text-muted-foreground hover:text-blue-400" title="Restart" data-testid={`restart-${agent.id}`}><RotateCcw className="w-4 h-4" /></button>
                                  )}
                                  {agent.status !== 'stopped' && (
                                    <button onClick={() => handleAction(agent.id, 'stop')} className="p-1.5 text-muted-foreground hover:text-red-400" title="Stop" data-testid={`stop-${agent.id}`}><Square className="w-4 h-4" /></button>
                                  )}
                                  <button onClick={() => openAgent(agent)} className="p-1.5 text-muted-foreground hover:text-white" title="Details"><Eye className="w-4 h-4" /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : detailLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
          ) : (
            /* AGENT DETAIL */
            <div className="space-y-6 max-w-5xl" data-testid="agent-detail">
              {/* Status + Controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-mono px-2 py-1 border ${getStatusBadge(selectedAgent.status, selectedAgent.health).cls}`}>
                    {getStatusBadge(selectedAgent.status, selectedAgent.health).label}
                  </span>
                  <span className="text-sm text-muted-foreground font-mono">v{selectedAgent.blueprint_version}</span>
                </div>
                <div className="flex gap-2">
                  {selectedAgent.status === 'running' && (
                    <Button onClick={() => handleAction(selectedAgent.id, 'pause')} variant="outline" size="sm" className="border-amber-500/20 text-amber-400 rounded-sm h-8 text-xs" data-testid="detail-pause"><Pause className="w-3 h-3 mr-1" /> Pause</Button>
                  )}
                  {selectedAgent.status === 'paused' && (
                    <Button onClick={() => handleAction(selectedAgent.id, 'resume')} size="sm" className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-8 text-xs" data-testid="detail-resume"><Play className="w-3 h-3 mr-1" /> Resume</Button>
                  )}
                  {selectedAgent.status !== 'running' && (
                    <Button onClick={() => handleAction(selectedAgent.id, 'restart')} variant="outline" size="sm" className="border-blue-500/20 text-blue-400 rounded-sm h-8 text-xs" data-testid="detail-restart"><RotateCcw className="w-3 h-3 mr-1" /> Restart</Button>
                  )}
                </div>
              </div>

              {/* Metrics Cards */}
              {agentMetrics && (
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="metrics-cards">
                  {[
                    { label: 'Success Rate', value: `${agentMetrics.success_rate}%`, cls: agentMetrics.success_rate >= 90 ? 'text-emerald-400' : agentMetrics.success_rate >= 70 ? 'text-amber-400' : 'text-red-400' },
                    { label: 'Avg Duration', value: `${agentMetrics.metrics?.avg_duration_ms || 0}ms`, cls: 'text-blue-400' },
                    { label: 'Total Runs', value: agentMetrics.metrics?.total_runs || 0, cls: 'text-white' },
                    { label: 'Runs 24h', value: agentMetrics.runs_last_24h, cls: 'text-violet-400' },
                    { label: 'Failures', value: agentMetrics.health?.consecutive_failures || 0, cls: (agentMetrics.health?.consecutive_failures || 0) > 0 ? 'text-red-400' : 'text-emerald-400' },
                  ].map((m, i) => (
                    <div key={i} className="bg-[#0a0a0a] border border-white/5 p-4">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{m.label}</p>
                      <p className={`font-mono text-xl font-bold ${m.cls}`}>{m.value}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Usage 24h */}
              {agentMetrics?.usage_last_24h && Object.keys(agentMetrics.usage_last_24h).length > 0 && (
                <div className="bg-[#0a0a0a] border border-white/5 p-4" data-testid="usage-24h">
                  <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Usage (24h)</h3>
                  <div className="flex gap-4">
                    {Object.entries(agentMetrics.usage_last_24h).map(([provider, data]) => (
                      <div key={provider} className="text-center">
                        <p className="text-[10px] text-muted-foreground uppercase">{provider}</p>
                        <p className="font-mono text-sm font-bold text-white">{data.units}</p>
                        <p className="text-[10px] text-muted-foreground">${data.cost}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Runs */}
              <div className="bg-[#0a0a0a] border border-white/5" data-testid="runs-table">
                <div className="p-4 border-b border-white/5">
                  <h3 className="text-xs text-muted-foreground uppercase tracking-wider">Recent Runs</h3>
                </div>
                {agentRuns.length > 0 ? (
                  <div className="divide-y divide-white/5">
                    {agentRuns.map(run => {
                      const rs = getRunStatus(run.status);
                      const RunIcon = rs.icon;
                      return (
                        <div key={run.id} className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors cursor-pointer" onClick={() => openTrace(selectedAgent.id, run.id)} data-testid={`run-${run.id}`}>
                          <div className="flex items-center gap-3">
                            <RunIcon className={`w-4 h-4 ${rs.cls}`} />
                            <div>
                              <p className="text-sm font-medium">{run.status}</p>
                              <p className="text-xs text-muted-foreground">{new Date(run.started_at).toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-mono text-xs text-muted-foreground">{run.duration_ms}ms</span>
                            <span className="font-mono text-xs text-muted-foreground">{(run.trace_json || []).length} steps</span>
                            {run.error_message && <span className="text-xs text-red-400 max-w-[200px] truncate">{run.error_message}</span>}
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground text-sm">No runs yet</div>
                )}
              </div>

              {/* Trace Viewer */}
              {selectedTrace && (
                <div className="bg-[#0a0a0a] border border-white/5 p-4" data-testid="trace-viewer">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs text-muted-foreground uppercase tracking-wider">Execution Trace</h3>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 ${selectedTrace.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{selectedTrace.status?.toUpperCase()}</span>
                      <span className="text-xs text-muted-foreground font-mono">{selectedTrace.duration_ms}ms</span>
                      <button onClick={() => setSelectedTrace(null)} className="p-1 text-muted-foreground hover:text-white"><XCircle className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {(selectedTrace.trace_json || []).map((step, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 flex items-center justify-center rounded-full text-[10px] font-mono ${
                            step.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                            step.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                            'bg-white/10 text-muted-foreground'
                          }`}>{step.step || i + 1}</div>
                          {i < (selectedTrace.trace_json || []).length - 1 && <div className="w-px h-5 bg-white/10 mt-1" />}
                        </div>
                        <div className="flex-1 pb-1">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium">{step.action || step.state_id}</p>
                            <span className="text-[10px] text-muted-foreground font-mono">{step.duration_ms}ms</span>
                          </div>
                          {step.output && Object.keys(step.output).length > 0 && (
                            <pre className="text-[10px] text-muted-foreground mt-1 font-mono bg-black/30 p-1.5 overflow-x-auto">{JSON.stringify(step.output, null, 2).slice(0, 200)}</pre>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedTrace.error_message && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <p className="text-xs text-red-400">Error: {selectedTrace.error_message}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AgentMonitor;
